'use client';

import { FormEvent, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, InputBase, Menu, MenuItem, Skeleton, Typography, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AllInboxOutlinedIcon from '@mui/icons-material/AllInboxOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import { avatarTone, chatHandle, chatPreview, isChatChannel, listTime, MAIL_FOLDERS, mailboxName, markParts, SENT_LAST_REASON } from '@/lib/inbox/display';
import LabelPicker, { FOLDER_VIEWS } from './LabelPicker';
import MailboxTag from './MailboxTag';
import MailLabels from './MailLabels';
import PullToRefresh from './PullToRefresh';
import SenderAvatar from './SenderAvatar';
import SwipeActions, { type SwipeSide } from './SwipeActions';
import { ACTION_VIEWS, handledToggle, readToggle, rowActions, STAR } from './conversationActions';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import RowMenu, { useLongPress, type RowMenuAction } from './RowMenu';
import { EASE_SHEET, enter, fadeUp, pop, press, reducedMotion } from './motion';
import { haptic } from './haptics';
import { useCopy } from './useCopy';
import { channelIcon } from './channelIcons';
import type { InboxMailLabel } from '@/lib/inbox/gmailLabels';
import type { InboxAccountInfo, InboxConversationAction, InboxMailFolder, InboxSummary } from '@/lib/inbox/types';

export const INK = 'var(--ink)';
export const ON_INK = 'var(--on-ink)';
export const MUTED = 'var(--muted)';
export const RULE = 'var(--rule)';
export const SELECTED = 'var(--wash)';
export const SURFACE = 'var(--surface)';
export const WASH = 'var(--wash)';
export const CANVAS = 'var(--canvas)';
export const DANGER = 'var(--danger)';
export const NEEDS_REPLY = { fg: 'var(--needs-reply-fg)', bg: 'var(--needs-reply-bg)' };

function swipeSide(action: InboxConversationAction): SwipeSide {
  const view = ACTION_VIEWS[action];
  return { label: view.short, Icon: view.Icon, color: view.color ?? (action === 'read' || action === 'unread' ? UNREAD : INK) };
}
const UNREAD = 'var(--unread)';
const UNREAD_BG = 'var(--unread-bg)';
const ROW_GUTTER = 16;
const ROW_GAP = 13;
const ROW_AVATAR = 52;
const WA_UNREAD = '#1daa61';
const WA_BADGE = '#25d366';
const ROW_DIVIDER = 'var(--rule)';
const rowDivider = { content: '""', position: 'absolute', left: ROW_GUTTER + ROW_AVATAR + ROW_GAP, right: 0, bottom: 0, height: '1px', bgcolor: ROW_DIVIDER } as const;

type Props = {
  conversations: InboxSummary[];
  selectedId: string;
  loading: boolean;
  loaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  unread: boolean;
  focus: boolean;
  focusCount: number;
  draftQuery: string;
  activeQuery: string;
  channelLabel: (key: string) => string;
  showChannel: boolean;
  allLabel: string;
  /** The channel on screen; the search field leads with its icon and name so it is clear which inbox this is. */
  channel: string;
  showAccount: boolean;
  onOpen: (id: string, reply?: boolean, messageId?: string) => void;
  onDraftQuery: (value: string) => void;
  onSearch: (query: string) => void;
  onView: (view: View) => void;
  onRefresh: () => Promise<unknown> | void;
  onLoadMore: () => void;
  unreadCount?: number;
  onCompose?: () => void;
  /** Runs a conversation action: swipe right toggles read, swipe left toggles handled, and right-click or long-press lists both. */
  onAction?: (id: string, action: InboxConversationAction) => void;
  onReply?: (id: string) => void;
  mailboxes: InboxAccountInfo[];
  mailbox: string;
  onMailbox: (account: string) => void;
  labels: InboxMailLabel[];
  label: string;
  onLabel: (label: string) => void;
  /** Gmail's Sent or Starred, open in place of the inbox. */
  folder?: InboxMailFolder;
  onFolder?: (folder?: InboxMailFolder) => void;
};

export const PICKER_PAPER_SX = { mt: 0.75, minWidth: 220, borderRadius: 2.5, boxShadow: '0 12px 32px rgba(20,20,18,0.14), 0 0 0 1px rgba(20,20,18,0.06)' };
export const PICKER_ITEM_SX = { gap: 1.25, py: 1, '&.Mui-selected': { bgcolor: SELECTED }, '&.Mui-selected:hover': { bgcolor: SELECTED } };

function MailboxPicker({ mailboxes, value, onChange }: { mailboxes: InboxAccountInfo[]; value: string; onChange: (account: string) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = mailboxes.find((box) => box.id === value);
  const dark = useTheme().palette.mode === 'dark';
  const tone = current ? avatarTone(current.label, dark) : null;
  const choose = (account: string) => {
    setAnchor(null);
    if (account !== value) onChange(account);
  };
  return (
    <>
      <ButtonBase
        aria-label={current ? `Inbox: ${current.label}. Change inbox` : 'Inbox: all. Change inbox'}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title={current?.address ?? 'All inboxes'}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          flexShrink: 0,
          height: 38,
          pl: 1.25,
          pr: 0.75,
          gap: 0.5,
          borderRadius: 2,
          bgcolor: tone ? tone.bg : WASH,
          color: tone ? tone.fg : INK,
          '&:focus-visible': { outline: `2px solid ${INK}`, outlineOffset: 2 },
        }}
      >
        <InboxOutlinedIcon sx={{ fontSize: 17, flexShrink: 0, opacity: 0.8 }} />
        <ExpandMoreRoundedIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.6 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: PICKER_PAPER_SX } }}
      >
        {[{ id: '', label: '', address: '' }, ...mailboxes].map((box) => {
          const boxTone = box.id ? avatarTone(box.label, dark) : { bg: INK, fg: ON_INK };
          return (
            <MenuItem
              key={box.id || 'all'}
              selected={box.id === value}
              onClick={() => choose(box.id)}
              sx={PICKER_ITEM_SX}
            >
              <Box
                aria-hidden
                sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, bgcolor: box.id ? boxTone.fg : 'transparent', boxShadow: box.id ? 'none' : `inset 0 0 0 1.5px ${INK}` }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: INK, lineHeight: 1.3 }}>
                  {box.id ? mailboxName(box.label) : 'All inboxes'}
                </Typography>
                {box.id && (
                  <Typography noWrap sx={{ fontSize: '0.75rem', color: MUTED, lineHeight: 1.3 }}>
                    {box.address}
                  </Typography>
                )}
              </Box>
              {box.id === value && <CheckRoundedIcon sx={{ fontSize: 18, color: INK }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

/** Stands in for the tabs while Sent or Starred is open: Focus and Unread mean nothing there. The × goes back to the inbox. */
function FolderHeading({ folder, onClose }: { folder: InboxMailFolder; onClose: () => void }) {
  const { Icon, color } = FOLDER_VIEWS[folder];
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, height: 36, pl: 1.25, pr: 0.5, borderRadius: 2.5, bgcolor: WASH, ...enter(fadeUp, 200) }}>
      <Icon aria-hidden sx={{ fontSize: 18, color }} />
      <Typography component="h2" noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 600, color: INK }}>
        {MAIL_FOLDERS[folder].label}
      </Typography>
      <IconButton aria-label="Back to the inbox" title="Back to the inbox" onClick={onClose} sx={{ width: 30, height: 30, color: MUTED, '&:hover': { color: INK } }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

function FolderIcon({ folder }: { folder: InboxMailFolder }) {
  const { Icon } = FOLDER_VIEWS[folder];
  return <Icon />;
}

type View = 'all' | 'focus' | 'unread';
type ViewOption = { value: View; label: string; count?: number; tone?: { bg: string; fg: string } };

function ViewSwitch({ value, options, onChange }: { value: View; options: ViewOption[]; onChange: (view: View) => void }) {
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );
  return (
    <Box role="tablist" aria-label="Show" sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', gap: '2px', p: '3px', height: 36, borderRadius: 2.5, bgcolor: WASH }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 3,
          bottom: 3,
          left: 3,
          width: `calc((100% - 6px - ${(options.length - 1) * 2}px) / ${options.length})`,
          borderRadius: 2,
          bgcolor: SURFACE,
          boxShadow: '0 1px 2px rgba(20,20,18,0.08), 0 0 0 0.5px rgba(20,20,18,0.06)',
          transform: `translateX(calc(${index} * (100% + 2px)))`,
          transition: `transform 320ms ${EASE_SHEET}`,
          ...reducedMotion,
        }}
      />
      {options.map((option) => {
        const active = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (active) return;
              haptic();
              onChange(option.value);
            }}
            sx={{
              position: 'relative',
              flex: '1 1 0',
              minWidth: 0,
              px: 0.75,
              gap: 0.75,
              borderRadius: 2,
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: active ? INK : MUTED,
              transition: 'color 200ms ease',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { color: INK },
              '&:focus-visible': { outline: `2px solid ${INK}`, outlineOffset: 1 },
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {option.label}
            </Box>
            {Boolean(option.count) && option.tone && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  minWidth: 18,
                  height: 18,
                  px: 0.625,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  bgcolor: option.tone.bg,
                  color: option.tone.fg,
                  ...enter(pop, 260),
                }}
              >
                {option.count! > 99 ? '99+' : option.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

type EmptyStateProps = {
  icon: ReactNode;
  tone: { bg: string; fg: string };
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
};

function EmptyState({ icon, tone, title, body, action }: EmptyStateProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', px: 4, pt: { xs: 9, md: 12 }, pb: 6, ...enter(fadeUp, 320) }}>
      <Box sx={{ position: 'relative', width: 64, height: 64, mb: 2.25 }}>
        <Box aria-hidden sx={{ position: 'absolute', inset: -10, borderRadius: '50%', bgcolor: tone.bg, opacity: 0.45 }} />
        <Box
          sx={{
            position: 'relative',
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: tone.bg,
            color: tone.fg,
            '& svg': { fontSize: 30 },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '1.0625rem', fontWeight: 650, color: INK, letterSpacing: '-0.01em' }}>{title}</Typography>
      <Typography sx={{ mt: 0.75, maxWidth: 300, fontSize: '0.875rem', lineHeight: 1.5, color: MUTED }}>{body}</Typography>
      {action && (
        <Button
          onClick={action.onClick}
          variant="outlined"
          size="small"
          sx={{ mt: 2.5, px: 2, height: 34, borderRadius: 999, borderColor: RULE, color: INK, fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: INK, bgcolor: 'transparent' } }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}

function RowSkeleton() {
  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: `${ROW_GAP}px`, pl: `${ROW_GUTTER}px`, pr: 2, py: 1.25, '&::after': rowDivider }}>
      <Skeleton variant="circular" width={ROW_AVATAR} height={ROW_AVATAR} sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 22 }}>
          <Skeleton width="46%" height={16} />
          <Skeleton width={40} height={12} sx={{ ml: 'auto' }} />
        </Box>
        <Skeleton width="72%" height={16} sx={{ mt: 0.25 }} />
      </Box>
    </Box>
  );
}

export default function ConversationList(props: Props) {
  const { conversations, selectedId, loading, loaded } = props;
  const knownRows = useRef(new Set(conversations.map((row) => row.id)));
  const [menu, setMenu] = useState<{ id: string; rect: DOMRect } | null>(null);
  const menuRow = menu && conversations.find((row) => row.id === menu.id);
  const longPress = useLongPress((target) => {
    const id = target.dataset.row;
    if (id) setMenu({ id, rect: target.getBoundingClientRect() });
  });

  const copy = useCopy();
  const menuActions = (row: InboxSummary): RowMenuAction[] => [
    ...(props.onReply ? [{ label: 'Reply', icon: <ReplyRoundedIcon />, onSelect: () => props.onReply?.(row.id) }] : []),
    ...(!isChatChannel(row.channel) && row.counterpart_handle.includes('@')
      ? [{ label: 'Copy email address', icon: <ContentCopyRoundedIcon />, onSelect: () => void copy(row.counterpart_handle, 'Email address') }]
      : []),
    ...(props.onAction
      ? rowActions(row).map((action) => {
          const view = ACTION_VIEWS[action];
          return { label: view.label, icon: <view.Icon />, color: view.color, onSelect: () => props.onAction?.(row.id, action) };
        })
      : []),
  ];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSearch(props.draftQuery.trim());
    // The phone's Search key should put the keyboard away so the results are visible.
    (event.currentTarget as HTMLFormElement).querySelector('input')?.blur();
  };

  const clearSearch = () => {
    props.onDraftQuery('');
    props.onSearch('');
  };
  const currentMailbox = props.mailboxes.find((box) => box.id === props.mailbox);
  const inMailbox = currentMailbox ? ` in ${mailboxName(currentMailbox.label)}` : '';
  const chats = props.allLabel === 'All chats';
  const ChannelIcon = channelIcon(props.channel);
  const viewAll = `View ${props.allLabel.toLowerCase()}`;
  const folderEmpty: EmptyStateProps | null =
    props.folder === 'sent'
      ? { icon: <FolderIcon folder="sent" />, tone: { bg: SELECTED, fg: MUTED }, title: props.activeQuery ? 'No matches in Sent' : 'No sent mail', body: props.activeQuery ? `Nothing you sent${inMailbox} matches “${props.activeQuery}”.` : `Mail sent${inMailbox} shows up here.`, action: { label: 'Back to the inbox', onClick: () => props.onFolder?.() } }
      : props.folder === 'starred'
        ? { icon: <FolderIcon folder="starred" />, tone: { bg: SELECTED, fg: MUTED }, title: props.activeQuery ? 'No matches in Starred' : 'No starred mail', body: props.activeQuery ? `Nothing starred${inMailbox} matches “${props.activeQuery}”.` : 'Star a conversation to keep it here.', action: { label: 'Back to the inbox', onClick: () => props.onFolder?.() } }
        : null;
  const empty: EmptyStateProps = folderEmpty ?? (props.activeQuery
    ? {
        icon: <SearchIcon />,
        tone: { bg: SELECTED, fg: MUTED },
        title: 'No matches',
        body: chats
          ? `Nothing${inMailbox} matches “${props.activeQuery}”. Try a name, a phone number, or a word from a message.`
          : `Nothing${inMailbox} matches “${props.activeQuery}”. Try a name, an email address or a word from the subject.`,
        action: { label: 'Clear search', onClick: clearSearch },
      }
    : props.label
      ? {
          icon: <LabelOutlinedIcon />,
          tone: { bg: SELECTED, fg: MUTED },
          title: 'Nothing here with this label',
          body: `No mail${inMailbox} matches “${props.label}” in this view.`,
          action: { label: 'Show all mail', onClick: () => props.onLabel('') },
        }
      : props.focus
        ? {
            icon: <CheckRoundedIcon />,
            tone: NEEDS_REPLY,
            title: 'You’re all caught up',
            body: chats ? `No chats${inMailbox} are waiting on a reply. New customer questions land here first.` : `No mail${inMailbox} is waiting on a reply. New customer questions land here first.`,
            action: { label: viewAll, onClick: () => props.onView('all') },
          }
        : props.unread
          ? {
              icon: <DraftsOutlinedIcon />,
              tone: { bg: UNREAD_BG, fg: UNREAD },
              title: chats ? 'No unread chats' : 'No unread mail',
              body: `You’ve read everything${inMailbox}. Swipe right on a thread to mark it unread again.`,
              action: { label: viewAll, onClick: () => props.onView('all') },
            }
          : {
              icon: <AllInboxOutlinedIcon />,
              tone: { bg: SELECTED, fg: MUTED },
              title: 'Your inbox is empty',
              body: 'New mail shows up here as it arrives. Search to find older conversations.',
            });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 0.75, px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Box
          component="form"
          onSubmit={submit}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            height: 38,
            borderRadius: 2,
            bgcolor: WASH,
            '&:focus-within': { bgcolor: SURFACE, boxShadow: `0 0 0 1px ${INK}` },
          }}
        >
          <ChannelIcon aria-hidden sx={{ fontSize: 19, color: INK }} />
          <InputBase
            fullWidth
            placeholder={`Search ${props.channelLabel(props.channel)}`}
            value={props.draftQuery}
            onChange={(event) => props.onDraftQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Search conversations', enterKeyHint: 'search' }}
            sx={{ fontSize: { xs: '1rem', sm: '0.875rem' }, color: INK }}
          />
          {props.draftQuery && (
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => {
                props.onDraftQuery('');
                if (props.activeQuery) props.onSearch('');
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        {loaded && !chats && props.onFolder && <LabelPicker labels={props.labels} value={props.label} folder={props.folder} onChange={props.onLabel} onFolder={props.onFolder} />}
        {loaded && props.mailboxes.length > 1 && <MailboxPicker mailboxes={props.mailboxes} value={props.mailbox} onChange={props.onMailbox} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 0.5, pb: 1, borderBottom: `1px solid ${RULE}` }}>
        {props.folder ? (
          <FolderHeading folder={props.folder} onClose={() => props.onFolder?.()} />
        ) : (
          <ViewSwitch
            value={props.focus ? 'focus' : props.unread ? 'unread' : 'all'}
            onChange={props.onView}
            options={[
              { value: 'all', label: props.allLabel },
              { value: 'focus', label: 'Focus', count: props.focusCount, tone: NEEDS_REPLY },
              { value: 'unread', label: 'Unread', count: props.unreadCount, tone: { bg: UNREAD_BG, fg: UNREAD } },
            ]}
          />
        )}
        {props.onCompose && (
          <IconButton
            aria-label="New message"
            onClick={props.onCompose}
            sx={{ ...press, ml: 0.75, width: 36, height: 36, bgcolor: INK, color: ON_INK, '&:hover': { bgcolor: INK }, '&:active': { transform: 'scale(0.92)' } }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
      <PullToRefresh onRefresh={props.onRefresh}>
        {loading && !conversations.length && Array.from({ length: 8 }, (_, index) => <RowSkeleton key={index} />)}
        {!loading && loaded && conversations.length === 0 && <EmptyState {...empty} />}
        {conversations.map((row, index) => {
          const fresh = !knownRows.current.has(row.id);
          if (fresh) knownRows.current.add(row.id);
          const right = props.onAction ? readToggle(row) : null;
          const left = props.onAction ? handledToggle(row) : null;
          return (
            <SwipeActions
              key={row.id}
              right={right && swipeSide(right)}
              left={left && swipeSide(left)}
              onSwipe={(direction) => {
                const action = direction === 'right' ? right : left;
                if (action) props.onAction?.(row.id, action);
              }}
            >
              <ConversationRow
                row={row}
                selected={row.id === selectedId}
                index={index}
                showAccount={props.showAccount}
                showChannel={props.showChannel}
                channelLabel={props.channelLabel}
                term={props.activeQuery}
                sentTo={props.folder === 'sent'}
                animate={fresh}
                onClick={() => props.onOpen(row.id, false, row.match?.id)}
                {...longPress}
              />
            </SwipeActions>
          );
        })}
        {props.hasMore && (
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button size="small" disabled={props.loadingMore} onClick={props.onLoadMore} sx={{ color: INK }}>
              {props.loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </PullToRefresh>
      {menuRow && menu && (
        <RowMenu
          anchor={menu.rect}
          preview={<ConversationRow row={menuRow} selected={false} index={0} showAccount={props.showAccount} showChannel={props.showChannel} channelLabel={props.channelLabel} still />}
          actions={menuActions(menuRow)}
          onOpenPreview={() => props.onOpen(menuRow.id)}
          onClose={() => setMenu(null)}
        />
      )}
    </Box>
  );
}

type RowProps = {
  row: InboxSummary;
  selected: boolean;
  index: number;
  showAccount: boolean;
  showChannel: boolean;
  channelLabel: (key: string) => string;
  term?: string;
  /** In Sent, the person is who it went to, as Gmail writes it. */
  sentTo?: boolean;
  still?: boolean;
  animate?: boolean;
} & Omit<HTMLAttributes<HTMLElement>, 'color'>;

function ConversationRow({ row, selected, index, showAccount, showChannel, channelLabel, term = '', sentTo = false, still = false, animate = false, ...handlers }: RowProps) {
  const chat = isChatChannel(row.channel);
  const name = row.counterpart_name || (chat ? chatHandle(row.channel, row.counterpart_handle) : row.counterpart_handle) || 'Unknown sender';
  const tone = avatarTone(row.counterpart_handle || name, useTheme().palette.mode === 'dark');
  const preview = row.match ?? chatPreview(row.snippet || (chat ? 'Media' : ''));
  const outbound = preview.from === 'You';
  const delay = useRef(Math.min(index, 12) * 22);
  const [entering, setEntering] = useState(animate && !still);
  useEffect(() => {
    if (!entering) return;
    const timer = window.setTimeout(() => setEntering(false), 260 + delay.current);
    return () => window.clearTimeout(timer);
  }, [entering]);
  return (
    <Box
      component={still ? 'div' : 'button'}
      {...(still ? {} : { type: 'button', 'data-row': row.id, 'aria-current': selected || undefined })}
      {...handlers}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: `${ROW_GAP}px`,
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: INK,
        border: 0,
        bgcolor: selected ? SELECTED : SURFACE,
        cursor: 'pointer',
        pl: `${ROW_GUTTER}px`,
        pr: 2,
        py: 1.25,
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        userSelect: 'none',
        ...(still
          ? {}
          : {
              '&::after': rowDivider,
              transition: 'background-color 160ms ease',
              ...(entering ? enter(fadeUp, 260, delay.current) : {}),
              // Hover only with a mouse: on iOS :hover sticks to the last row touched.
              '@media (hover: hover)': { '&:hover': { bgcolor: selected ? SELECTED : WASH } },
              // A short delay before the press shows, like a native list, so a row does not flash when a scroll starts on it.
              '&:active': { bgcolor: SELECTED, transition: 'background-color 0ms linear 80ms' },
              '&:focus-visible': { outline: `2px solid ${INK}`, outlineOffset: -2 },
            }),
      }}
    >
      <SenderAvatar name={name} email={row.counterpart_handle} size={ROW_AVATAR} bg={tone.bg} fg={tone.fg} group={row.group} picture={row.avatar} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minHeight: 22 }}>
          <Typography noWrap sx={{ flex: 1, fontSize: '1.0625rem', lineHeight: '22px', fontWeight: row.unread ? 600 : 400, color: INK }}>
            {sentTo && (
              <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>
                To:{' '}
              </Box>
            )}
            {name}
          </Typography>
          {showAccount && row.account_label && <MailboxTag label={row.account_label} size="sm" />}
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: '0.75rem',
              lineHeight: '22px',
              fontWeight: row.unread ? 600 : 400,
              fontVariantNumeric: 'tabular-nums',
              color: row.unread ? (chat ? WA_UNREAD : UNREAD) : MUTED,
            }}
          >
            {listTime(row.match?.sent_at ?? row.last_message_at)}
          </Typography>
        </Box>
        {chat ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.125, minHeight: 20 }}>
            <Typography component="span" noWrap sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.375, fontSize: '0.875rem', lineHeight: '20px', color: MUTED }}>
              {outbound && <DoneAllRoundedIcon aria-hidden sx={{ fontSize: 16, color: MUTED, ml: '-1px' }} />}
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.group && preview.from && !outbound && `${preview.from}: `}
                {markParts(preview.text || 'Media', row.match ? term : '').map((part, partIndex) =>
                  part.hit ? (
                    <Box key={partIndex} component="span" sx={{ color: INK, fontWeight: 600 }}>
                      {part.text}
                    </Box>
                  ) : (
                    part.text
                  )
                )}
              </Box>
            </Typography>
            {showChannel && (
              <Typography sx={{ flexShrink: 0, fontSize: '0.6875rem', color: MUTED, border: `1px solid ${RULE}`, borderRadius: 1, px: 0.5 }}>{channelLabel(row.channel)}</Typography>
            )}
            <RowDots needsReply={row.needs_reply} replyReason={row.reply_reason} unread={row.unread} unreadColor={WA_BADGE} still={still} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              <Typography noWrap sx={{ flex: 1, fontSize: '0.875rem', lineHeight: '20px', fontWeight: row.unread ? 500 : 400, color: INK }}>
                {row.title || 'No subject'}
              </Typography>
              <RowMarks showChannel={showChannel} channel={channelLabel(row.channel)} />
              {row.starred && <StarRoundedIcon aria-label="Starred" sx={{ flexShrink: 0, fontSize: 16, color: STAR }} />}
              <RowDots needsReply={row.needs_reply} replyReason={row.reply_reason} unread={row.unread} unreadColor={UNREAD} still={still} />
            </Box>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: '0.875rem',
                lineHeight: '19px',
                color: MUTED,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                // A row with labels gives them the preview's second line, so it stays the same height.
                WebkitLineClamp: row.gmail_labels.length ? 1 : 2,
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {row.reply_reason === SENT_LAST_REASON && (
                <Box component="span" sx={{ color: INK }}>
                  You:{' '}
                </Box>
              )}
              {row.snippet}
            </Typography>
            <MailLabels labels={row.gmail_labels} />
          </>
        )}
      </Box>
    </Box>
  );
}

function StatusDot({ label, title, color, still }: { label: string; title?: string; color: string; still?: boolean }) {
  return (
    <Box
      role="img"
      aria-label={label}
      title={title || label}
      sx={{
        flexShrink: 0,
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        ...(still ? {} : enter(pop, 280)),
      }}
    />
  );
}

function RowDots({
  needsReply,
  replyReason,
  unread,
  unreadColor,
  still = false,
}: {
  needsReply: boolean | null;
  replyReason: string;
  unread: boolean;
  unreadColor: string;
  still?: boolean;
}) {
  if (!needsReply && !unread) return null;
  return (
    <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', minHeight: 20 }}>
      {needsReply && <StatusDot label={replyReason ? `Needs reply: ${replyReason}` : 'Needs reply'} title={replyReason || 'Needs reply'} color={NEEDS_REPLY.fg} still={still} />}
      {unread && <StatusDot label="Unread" color={unreadColor} still={still} />}
    </Box>
  );
}

function RowMarks({ showChannel, channel }: { showChannel: boolean; channel: string }) {
  if (!showChannel) return null;
  return (
    <Typography sx={{ flexShrink: 0, fontSize: '0.6875rem', color: MUTED, border: `1px solid ${RULE}`, borderRadius: 1, px: 0.5 }}>{channel}</Typography>
  );
}
